import {
    WindowWithPbjs,
    WindowWithClientData,
    WindowWithDataLayer,
    WindowWithGa,
    WindowWithGoogleTag,
    WindowWithAdvertising,
    WindowWithAdSense,
} from '@gumtree/shared/src/types/window';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { getCurrentBreakpoint } from '@gumtree/shared/src/util/breakpoints-service';
import { convertBooleanToAB } from '@gumtree/shared/src/util/experiments-service';
import { findInitialDataLayerEntry } from '@gumtree/shared/src/util/data-layer-service';
import { yieldToMain } from '@gumtree/shell/src/utils/yield-to-main';

import initLiveRamp from './framework/liveramp/init';
import dfpTrackBrowserType from './framework/dfp/browser-key-value-builder';
import dfpTrackScreenResolution from './framework/dfp/screen-resolution-key-value';
import dfpTrackTallMobile from './framework/dfp/tall-mobile-key-value';
import dfpTrackBelow360px from './framework/dfp/below-360px-key-value';
import dfpBffSrpResultsSize from './framework/dfp/results-size-key-value';
import dfpTrackLocationId from './framework/dfp/location-id-key-value';
import loadDisplayAds from './framework/dfp/init-display-ads';
import startPrebid from './framework/prebid/start-prebid';
import trackAdsPerformance from './framework/track-ads-performance';
import initMicrosoftClarity from './framework/microsoft-clarity/init';
import { extractPageData } from './framework/util/client-data-extractor';
import {
    featuresToKeyValue,
    GrowthBookAdFeatures,
    getCmpRepromptValue,
} from './framework/util/ad-experiment-utils';

const initDisplay = async (
    window: WindowWithGoogleTag &
        WindowWithDataLayer &
        WindowWithClientData &
        WindowWithGa &
        WindowWithAdvertising
) => {
    const { device, pageType, l1Category, l2Category } = extractPageData(window);
    await yieldToMain();

    const baseDataLayer = window.dataLayer && findInitialDataLayerEntry(window.dataLayer);
    const hashedUserIdFromDataLayer = baseDataLayer?.d?.ck;

    await yieldToMain();

    const serverConfigs = {
        ...window.clientData.displayAds,
        ...window.clientData.displayAds?.config,
    };

    const userType = window.clientData.userType;

    if (serverConfigs?.targeting && (userType === 'STANDARD' || userType === 'PRO')) {
        serverConfigs.targeting.user_type = userType;
    }

    await yieldToMain();

    if (serverConfigs?.targeting?.ptg) {
        const trackedGamExperiments = {
            ...GrowthBookAdFeatures,
        };
        const growthBookExperiments = featuresToKeyValue(
            trackedGamExperiments,
            convertBooleanToAB
        ).map(([experimentId, value]) => `${experimentId}-${value}`);
        serverConfigs.targeting.ptg = growthBookExperiments;
    }

    const cmpRepromptValue = getCmpRepromptValue();
    if (cmpRepromptValue && serverConfigs?.targeting) {
        serverConfigs.targeting.cmp_reprompt = cmpRepromptValue;
    }
    await yieldToMain();

    const adExperimentKeyValues = featuresToKeyValue(GrowthBookAdFeatures);

    const isNearbyAdsFeatureEnabled =
        adExperimentKeyValues.find((feature) => feature[0] === 'nearby-listing-new-ads')?.[1] ===
        'B';

    const nearbyAdsCount = window.clientData?.resultsPage?.nearbyAds?.ads?.length || 0;
    const isMobile = device === 'mobile';
    const showNearbySlot1 =
        isNearbyAdsFeatureEnabled &&
        ((isMobile && nearbyAdsCount > 6) || (!isMobile && nearbyAdsCount > 10));
    const showNearbySlot2 =
        isNearbyAdsFeatureEnabled &&
        ((isMobile && nearbyAdsCount > 10) || (!isMobile && nearbyAdsCount > 20));

    const showNearbyAds = { showNearbySlot1, showNearbySlot2 };
    await yieldToMain();

    const gam02Variant = adExperimentKeyValues.find((feature) => feature[0] === 'GAM02')?.[1];

    await loadDisplayAds(
        window.googletag,
        window,
        serverConfigs,
        window.clientData.displayAds?.config?.resultsSize,
        l1Category,
        l2Category ? l2Category : null,
        pageType,
        device,
        hashedUserIdFromDataLayer,
        showNearbyAds,
        gam02Variant,
        adExperimentKeyValues
    );

    await yieldToMain();
    dfpTrackTallMobile(window.googletag);
    dfpTrackBelow360px(window.googletag);
    dfpBffSrpResultsSize(window.googletag, window, pageType);
    dfpTrackBrowserType(window.googletag);
    dfpTrackScreenResolution(window.googletag);
    await dfpTrackLocationId(window.googletag);

    trackAdsPerformance({ googletag: window.googletag, ga: window.ga, performance });
};

const initPrebid = async (
    window: WindowWithGoogleTag & WindowWithPbjs & WindowWithClientData & WindowWithDataLayer
) => {
    const nexx360BiddersVariant =
        featuresToKeyValue(GrowthBookAdFeatures).find(
            ([experimentId]) => experimentId === GrowthBookFeature.NEXX360_BIDDERS
        )?.[1] ?? 'OFF';
    await yieldToMain();

    const isNexx360 = nexx360BiddersVariant === 'B';
    await yieldToMain();

    const { device, pageType } = extractPageData(window);
    await yieldToMain();

    const l1Category = window.clientData.displayAds?.config?.l1Category;
    const l2Category = window.clientData.displayAds?.config?.l2Category;
    const l3Category = window.clientData.displayAds?.config?.targeting?.l3;
    const resultsSize = window.clientData.displayAds?.config?.resultsSize;
    const baseDataLayer = window.dataLayer && findInitialDataLayerEntry(window.dataLayer);
    const hashedUserIdFromDataLayer = baseDataLayer?.d?.ck;
    const ppid = window.clientData.displayAds?.config?.ppid || hashedUserIdFromDataLayer;
    await yieldToMain();

    const screenSize = getCurrentBreakpoint(window)?.tshirt;
    await yieldToMain();

    await startPrebid(
        window.googletag,
        pageType,
        device,
        screenSize,
        l1Category,
        l2Category,
        l3Category,
        '',
        window.clientData.displayAds,
        window,
        ppid,
        isNexx360,
        resultsSize
    );
};

const initAds = (
    window: WindowWithGoogleTag &
        WindowWithPbjs &
        WindowWithClientData &
        WindowWithDataLayer &
        WindowWithGa &
        WindowWithAdvertising &
        WindowWithAdSense
) => {
    if (!window.clientData) {
        throw new Error('clientData not found on window object. Error id: 1uw2');
    }

    const userEmail = window.clientData.userData?.email;
    initLiveRamp(window, userEmail);

    initMicrosoftClarity(window);

    // initDisplay must complete first to register the GUM.ADS.DISPLAY.LOAD event listener
    // before initPrebid can dispatch it. With yieldToMain() calls in both functions,
    // parallel execution creates a race condition where the event can fire before the
    // listener is registered, causing display ads to never load.
    initDisplay(window).then(() => initPrebid(window));
};

export default initAds;
